import type { ReactNode } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  type SortingStrategy,
} from '@dnd-kit/sortable'

import type { Modifier } from '@dnd-kit/core'

import { reorderIds } from '@/shared/lib/reorder'

/**
 * Drag-to-reorder for a list of things with numeric ids.
 *
 * The keyboard sensor is not an extra: reordering that only responds to a mouse
 * is reordering half the users cannot do at all. It is why the items below hang
 * their listeners off a dedicated handle rather than off the whole row -- a row
 * that is both a link and a drag source has no way to tell Enter-to-open from
 * Enter-to-pick-up.
 *
 * The pointer sensor waits for 8px of movement before it claims the gesture, so
 * a click on the handle is still a click and a drag that starts on it does not
 * have to be deliberate to a millimetre.
 */
export function SortableList({
  ids,
  strategy,
  modifiers = [],
  onReorder,
  children,
}: {
  ids: number[]
  strategy: SortingStrategy
  modifiers?: Modifier[]
  onReorder: (ids: number[]) => void
  children: ReactNode
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const next = reorderIds(ids, Number(event.active.id), toId(event.over?.id))
    if (next !== null) onReorder(next)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={modifiers}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={strategy}>
        {children}
      </SortableContext>
    </DndContext>
  )
}

function toId(id: string | number | undefined): number | null {
  return id === undefined ? null : Number(id)
}
